import {
  apiGetUserList,
  apiGetApproverList,
  apiGetCreatorList,
  apiGetCampaignUrlTypeList,
  apiGetCampaignUrlCreatorList,
  apiValidateCampaignKey,
  apiGetSourceChoices,
  apiGetContentTypesForCampaignURL,
  apiGetPartnerCardsByPlacement,
  apiGetAllPartnerCards,
  apiCreatePartnerCard,
  apiUpdatePartnerCard,
  apiDeletePartnerCard,
  apiUploadPartnerCardLogo,
  apiCreatePartnerCardCta,
  apiUpdatePartnerCardCta,
  apiDeletePartnerCardCta,
  apiGetSinoaPartnerMetrics,
  apiGetPartnerCardMetrics,
} from '../../../api/marketingModule';

export default {
  namespaced: true,

  state: () => ({
    userList: [],
    approverList: [],
    creatorList: [],
    campaignUrlTypeList: [],
    campaignUrlCreatorList: [],
    fetchedCampaign: null, // Campaign object - CampaignRegistration / Account Activation
    campaignCobranding: null,
    campaignErrorsRaw: null,
    campaignSourceChoices: [],
    partnerCards: [],
    partnerCardsLoading: false,
    partnerCardMetrics: [],
    partnerCardMetricsLoading: false,
    sinoaPartnerMetrics: [],
    sinoaPartnerQuestions: [],
    sinoaPartnerRegions: [],
    sinoaPartnerMetricsLoading: false,
  }),

  getters: {
    visiblePartnerCards: (state, getters, rootState, rootGetters) => {
      const userLocation = rootGetters['subscription/userLocationCode'];
      return state.partnerCards.filter(
        (card) => card.location === 'ALL' || card.location === userLocation,
      );
    },
  },

  mutations: {
    setUserList(state, value) {
      state.userList = value;
    },

    setApproverList(state, value) {
      state.approverList = value;
    },

    setCreatorList(state, value) {
      state.creatorList = value;
    },

    setCampaignUrlTypeList(state, value) {
      state.campaignUrlTypeList = value;
    },

    setCampaignUrlCreatorList(state, value) {
      state.campaignUrlCreatorList = value;
    },

    setFetchedCampaign(state, value) {
      state.fetchedCampaign = value;
    },

    setCampaignCobranding(state, value) {
      state.campaignCobranding = value;
    },

    setCampaignErrorsRaw(state, value) {
      state.campaignErrorsRaw = value;
    },

    setCampaignSourceChoices(state, value) {
      state.campaignSourceChoices = value;
    },

    setPartnerCards(state, value) {
      state.partnerCards = value;
    },

    setPartnerCardsLoading(state, value) {
      state.partnerCardsLoading = value;
    },

    removePartnerCard(state, cardId) {
      state.partnerCards = state.partnerCards.filter((card) => card.id !== cardId);
    },

    setPartnerCardMetrics(state, value) {
      state.partnerCardMetrics = value;
    },

    setPartnerCardMetricsLoading(state, value) {
      state.partnerCardMetricsLoading = value;
    },

    setSinoaPartnerMetrics(state, value) {
      state.sinoaPartnerMetrics = value;
    },

    setSinoaPartnerQuestions(state, value) {
      state.sinoaPartnerQuestions = value;
    },

    setSinoaPartnerRegions(state, value) {
      state.sinoaPartnerRegions = value;
    },

    setSinoaPartnerMetricsLoading(state, value) {
      state.sinoaPartnerMetricsLoading = value;
    },
  },

  actions: {
    async readUserList({ commit }) {
      try {
        const result = await apiGetUserList();
        commit('setUserList', result);
      } catch (e) {
        this._vm.$snackbar.error(e);
      }
    },
    async readApproverList({ commit }) {
      try {
        const result = await apiGetApproverList();
        commit('setApproverList', result);
      } catch (e) {
        this._vm.$snackbar.error(e);
      }
    },

    async readCreatorList({ commit }) {
      try {
        const result = await apiGetCreatorList();
        commit('setCreatorList', result);
      } catch (e) {
        this._vm.$snackbar.error(e);
      }
    },

    async readCampaignUrlTypeList({ commit }) {
      try {
        const result = await apiGetCampaignUrlTypeList();
        commit('setCampaignUrlTypeList', result);
      } catch (e) {
        this._vm.$snackbar.error(e);
      }
    },

    async readCampaignUrlCreatorList({ commit }) {
      try {
        const result = await apiGetCampaignUrlCreatorList();
        commit('setCampaignUrlCreatorList', result);
      } catch (e) {
        this._vm.$snackbar.error(e);
      }
    },

    async validateCampaignUrlKey({ commit }, key) {
      try {
        const { campaign, cobranding } = await apiValidateCampaignKey({
          activation_key: key,
        });
        commit('setFetchedCampaign', campaign);
        commit('setCampaignCobranding', cobranding);
      } catch (e) {
        commit('setCampaignErrorsRaw', JSON.parse(e.message));
      }
    },

    async readCampaignSourceChoices({ commit }) {
      try {
        const result = await apiGetSourceChoices();
        commit('setCampaignSourceChoices', result.sort());
      } catch (e) {
        this._vm.$snackbar.error(e);
      }
    },

    async getContentTypesForCampaignURL(ignored, campaignUrlId) {
      try {
        const result = await apiGetContentTypesForCampaignURL({
          id: campaignUrlId,
        });
        return result;
      } catch (e) {
        this._vm.$snackbar.error(e);
      }
      return [];
    },

    async fetchPartnerCards({ commit }) {
      commit('setPartnerCardsLoading', true);
      try {
        const cards = await apiGetAllPartnerCards();
        commit('setPartnerCards', cards);
      } catch (e) {
        this._vm.$snackbar.error('Failed to load partner cards.');
      } finally {
        commit('setPartnerCardsLoading', false);
      }
    },

    async fetchPartnerCardsByPlacement(_, { placement, location }) {
      try {
        return await apiGetPartnerCardsByPlacement(placement, location);
      } catch {
        return [];
      }
    },

    async savePartnerCard({ dispatch }, { cardData, ctas, logoFile, existingCard }) {
      try {
        let result;
        let cardId;

        if (existingCard) {
          result = await apiUpdatePartnerCard({ ...cardData, card_id: existingCard.id });
          cardId = existingCard.id;
        } else {
          result = await apiCreatePartnerCard(cardData);
          cardId = result.card.id;
        }

        if (!result.ok) {
          this._vm.$snackbar.error(result.error || 'Failed to save partner card.');
          return false;
        }

        if (logoFile) {
          await apiUploadPartnerCardLogo(cardId, logoFile);
        }

        await dispatch('syncPartnerCardCtas', { cardId, ctas, existingCtas: existingCard?.ctas || [] });
        await dispatch('fetchPartnerCards');

        this._vm.$snackbar.success(existingCard ? 'Partner card updated.' : 'Partner card created.');
        return true;
      } catch (e) {
        this._vm.$snackbar.error('Failed to save partner card.');
        return false;
      }
    },

    async syncPartnerCardCtas(_, { cardId, ctas, existingCtas }) {
      const existingIds = new Set(existingCtas.map((c) => c.id));
      const newCtaIds = new Set();

      const upsertPromises = ctas.map((cta) => {
        const ctaPayload = {
          type: cta.type,
          label: cta.label,
          value: cta.value,
          is_active: cta.is_active !== false,
          is_primary: cta.is_primary === true,
        };
        if (cta.id && existingIds.has(cta.id)) {
          newCtaIds.add(cta.id);
          return apiUpdatePartnerCardCta(cta.id, ctaPayload);
        }
        return apiCreatePartnerCardCta(cardId, ctaPayload);
      });

      await Promise.all(upsertPromises);

      const deletePromises = existingCtas
        .filter((existing) => !newCtaIds.has(existing.id))
        .map((existing) => apiDeletePartnerCardCta(existing.id));

      await Promise.all(deletePromises);
    },

    async deletePartnerCard({ commit }, cardId) {
      try {
        const result = await apiDeletePartnerCard(cardId);
        if (!result.ok) {
          this._vm.$snackbar.error(result.error || 'Failed to delete partner card.');
          return;
        }
        commit('removePartnerCard', cardId);
        this._vm.$snackbar.success('Partner card deleted.');
      } catch (e) {
        this._vm.$snackbar.error('Failed to delete partner card.');
      }
    },

    async fetchPartnerCardMetrics({ commit }, { excludeGfMembers = false } = {}) {
      commit('setPartnerCardMetricsLoading', true);
      try {
        const metrics = await apiGetPartnerCardMetrics(excludeGfMembers);
        commit('setPartnerCardMetrics', metrics);
      } catch (e) {
        this._vm.$snackbar.error('Failed to load partner card metrics.');
      } finally {
        commit('setPartnerCardMetricsLoading', false);
      }
    },

    async fetchSinoaPartnerMetrics({ commit }, { excludeGfMembers = false } = {}) {
      commit('setSinoaPartnerMetricsLoading', true);
      try {
        const result = await apiGetSinoaPartnerMetrics(excludeGfMembers);
        commit('setSinoaPartnerMetrics', result.metrics || []);
        commit('setSinoaPartnerQuestions', result.questions || []);
        commit('setSinoaPartnerRegions', result.regions || []);
      } catch (e) {
        this._vm.$snackbar.error('Failed to load Sinoa partner metrics.');
      } finally {
        commit('setSinoaPartnerMetricsLoading', false);
      }
    },
  },
};
